import React from 'react';
import styled from 'styled-components';
import ReactExport from 'react-data-export';
import { Line } from 'react-chartjs-2';
import { Ring } from 'react-awesome-spinners';
import palette from '../../lib/styles/palette';
import { MdChevronLeft, MdChevronRight, MdDateRange } from 'react-icons/md';
import MonthPicker from '../sales/MonthPicker';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const CHART_COLORS = [
  '#36A2EB',
  '#FF6384',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#7FDBFF',
];

const buildLineChartData = (rows, fields) => {
  var labels = rows.map(function (r) {
    return r.date.slice(5);
  });
  var datasets = fields.map(function (f, i) {
    return {
      label: f.label,
      data: rows.map(function (r) { return r[f.key]; }),
      borderColor: CHART_COLORS[i % CHART_COLORS.length],
      backgroundColor: 'transparent',
      pointRadius: 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      fill: false,
    };
  });
  return { labels: labels, datasets: datasets };
};

var lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    position: 'bottom',
    labels: {
      usePointStyle: true,
      fontSize: 12,
      padding: 16,
    },
  },
  scales: {
    xAxes: [{
      gridLines: { display: false },
      ticks: { fontSize: 10 },
    }],
    yAxes: [{
      ticks: { beginAtZero: true, fontSize: 11 },
      gridLines: { color: '#f0f2f5' },
    }],
  },
  tooltips: {
    mode: 'index',
    intersect: false,
  },
};

const Wrapper = styled.div`
  margin-top: 24px;
  padding-bottom: 40px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 32px;
  position: relative;
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  font-size: 1.5rem;
  color: #666;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${palette.webGray[1]};
    color: ${palette.webCyan[5]};
  }

  &:disabled {
    color: #d0d0d0;
    cursor: not-allowed;
    background: transparent;
  }
`;

const DateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 20px;
  background: ${palette.webGray[1]};
  cursor: pointer;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2c3e50;
  transition: background-color 0.15s ease;

  svg {
    font-size: 1rem;
    color: ${palette.webCyan[5]};
  }

  &:hover {
    background: ${palette.webGray[2]};
  }
`;

const MonthPickerWrapper = styled.div`
  position: absolute;
  top: 45px;
  z-index: 100;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 0.95rem;
  margin-bottom: 16px;
  text-align: center;
`;

const SectionWrapper = styled.div`
  margin-bottom: 40px;
  background: #fff;
  border: 1px solid #e0e4ea;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: #f8f9fb;
  border-bottom: 1px solid #e0e4ea;
`;

const SectionTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
`;

const ExcelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid ${palette.webCyan[5]};
  border-radius: 4px;
  background: #fff;
  color: ${palette.webCyan[5]};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${palette.webCyan[5]};
    color: #fff;
  }
`;

const SectionBody = styled.div`
  padding: 24px;
`;

const ChartWrapper = styled.div`
  height: 260px;
  margin-bottom: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    border: 1px solid #e0e4ea;
    padding: 8px 12px;
    text-align: center;
  }

  th {
    background: #f5f7fa;
    font-weight: 600;
    color: #2c3e50;
    white-space: nowrap;
  }

  td {
    color: #555;
  }

  tbody tr:hover {
    background: #f9fbfd;
  }
`;

const formatMonthLabel = (monthStr) => {
  var parts = monthStr.split('-');
  return parts[0] + '년 ' + parseInt(parts[1], 10) + '월';
};

const fmt = (n) => (n != null ? Number(n).toLocaleString() : '0');

var conversionFields = [
  { key: 'mobileActive', label: '모바일 액티브' },
  { key: 'routineEntry', label: '루틴 진입' },
  { key: 'challengeEntry', label: '챌린지 진입' },
  { key: 'playActive', label: '플레이' },
  { key: 'newUser', label: '신규유저수' },
];

var playTypeFields = [
  { key: 'aiRoutinePlay', label: 'AI루틴 플레이' },
  { key: 'otherPlay', label: '기타 플레이' },
  { key: 'challengePlay', label: '챌린지 플레이' },
];

var aiPremiumFields = [
  { key: 'newRecommend', label: 'AI루틴 신규추천' },
  { key: 'reRecommend', label: 'AI루틴 재추천' },
  { key: 'android', label: '신규 구독수 Android' },
  { key: 'ios', label: '신규 구독수 iOS' },
];

const AdminTrapAppStatistics = ({
  selectedMonth,
  onPrevMonth,
  onNextMonth,
  onChangeMonth,
  monthPickerVisible,
  onToggleMonthPicker,
  loading,
  error,
  tableData,
}) => {
  var monthDate = new Date(selectedMonth + '-01');

  return (
    <Wrapper>
      <Controls>
        <ArrowButton onClick={onPrevMonth} disabled={loading}>
          <MdChevronLeft />
        </ArrowButton>
        <DateButton onClick={onToggleMonthPicker}>
          <MdDateRange />
          {formatMonthLabel(selectedMonth)}
        </DateButton>
        <ArrowButton onClick={onNextMonth} disabled={loading}>
          <MdChevronRight />
        </ArrowButton>
        <MonthPickerWrapper>
          <MonthPicker
            visible={monthPickerVisible}
            onChangeMonth={onChangeMonth}
            date={monthDate}
            onToggleMonthPicker={onToggleMonthPicker}
          />
        </MonthPickerWrapper>
      </Controls>

      {error && <ErrorText>{error}</ErrorText>}

      {loading && (
        <LoadingWrapper>
          <Ring />
        </LoadingWrapper>
      )}

      {!loading && tableData && (
        <>
          {/* 전환율 */}
          <SectionWrapper>
            <SectionHeader>
              <SectionTitle>전환율</SectionTitle>
              <ExcelFile
                filename={selectedMonth + ' 전환율'}
                element={<ExcelButton>Excel 다운로드</ExcelButton>}
              >
                <ExcelSheet data={tableData.conversionData} name="전환율">
                  <ExcelColumn label="날짜" value="date" />
                  <ExcelColumn label="모바일 액티브" value="mobileActive" />
                  <ExcelColumn label="루틴 진입" value="routineEntry" />
                  <ExcelColumn label="루틴 진입률(%)" value="routineEntryRate" />
                  <ExcelColumn label="챌린지 진입" value="challengeEntry" />
                  <ExcelColumn label="챌린지 진입률(%)" value="challengeEntryRate" />
                  <ExcelColumn label="플레이" value="playActive" />
                  <ExcelColumn label="플레이 전환율(%)" value="playActiveRate" />
                  <ExcelColumn label="신규유저수" value="newUser" />
                </ExcelSheet>
              </ExcelFile>
            </SectionHeader>
            <SectionBody>
              <ChartWrapper>
                <Line
                  data={buildLineChartData(tableData.conversionData, conversionFields)}
                  options={lineChartOptions}
                />
              </ChartWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>모바일 액티브</th>
                    <th>루틴 진입</th>
                    <th>루틴 진입률(%)</th>
                    <th>챌린지 진입</th>
                    <th>챌린지 진입률(%)</th>
                    <th>플레이</th>
                    <th>플레이 전환율(%)</th>
                    <th>신규유저수</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.conversionData.map((row) => (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td>{fmt(row.mobileActive)}</td>
                      <td>{fmt(row.routineEntry)}</td>
                      <td>{row.routineEntryRate}%</td>
                      <td>{fmt(row.challengeEntry)}</td>
                      <td>{row.challengeEntryRate}%</td>
                      <td>{fmt(row.playActive)}</td>
                      <td>{row.playActiveRate}%</td>
                      <td>{fmt(row.newUser)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </SectionBody>
          </SectionWrapper>

          {/* 플레이 성격 */}
          <SectionWrapper>
            <SectionHeader>
              <SectionTitle>플레이 성격</SectionTitle>
              <ExcelFile
                filename={selectedMonth + ' 플레이 성격'}
                element={<ExcelButton>Excel 다운로드</ExcelButton>}
              >
                <ExcelSheet data={tableData.playTypeData} name="플레이 성격">
                  <ExcelColumn label="날짜" value="date" />
                  <ExcelColumn label="AI루틴 플레이" value="aiRoutinePlay" />
                  <ExcelColumn label="기타 플레이" value="otherPlay" />
                  <ExcelColumn label="챌린지 플레이" value="challengePlay" />
                </ExcelSheet>
              </ExcelFile>
            </SectionHeader>
            <SectionBody>
              <ChartWrapper>
                <Line
                  data={buildLineChartData(tableData.playTypeData, playTypeFields)}
                  options={lineChartOptions}
                />
              </ChartWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>AI루틴 플레이</th>
                    <th>기타 플레이</th>
                    <th>챌린지 플레이</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.playTypeData.map((row) => (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td>{fmt(row.aiRoutinePlay)}</td>
                      <td>{fmt(row.otherPlay)}</td>
                      <td>{fmt(row.challengePlay)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </SectionBody>
          </SectionWrapper>

          {/* AI루틴 추천 및 신규 구독수 */}
          <SectionWrapper>
            <SectionHeader>
              <SectionTitle>AI루틴 추천 및 신규 구독수</SectionTitle>
              <ExcelFile
                filename={selectedMonth + ' AI루틴 추천 및 신규 구독수'}
                element={<ExcelButton>Excel 다운로드</ExcelButton>}
              >
                <ExcelSheet data={tableData.aiPremiumData} name="AI루틴 추천 및 신규 구독수">
                  <ExcelColumn label="날짜" value="date" />
                  <ExcelColumn label="AI루틴 신규추천" value="newRecommend" />
                  <ExcelColumn label="AI루틴 재추천" value="reRecommend" />
                  <ExcelColumn label="신규 구독수 Android" value="android" />
                  <ExcelColumn label="신규 구독수 iOS" value="ios" />
                </ExcelSheet>
              </ExcelFile>
            </SectionHeader>
            <SectionBody>
              <ChartWrapper>
                <Line
                  data={buildLineChartData(tableData.aiPremiumData, aiPremiumFields)}
                  options={lineChartOptions}
                />
              </ChartWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>AI루틴 신규추천</th>
                    <th>AI루틴 재추천</th>
                    <th>신규 구독수 Android</th>
                    <th>신규 구독수 iOS</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.aiPremiumData.map((row) => (
                    <tr key={row.date}>
                      <td>{row.date}</td>
                      <td>{fmt(row.newRecommend)}</td>
                      <td>{fmt(row.reRecommend)}</td>
                      <td>{fmt(row.android)}</td>
                      <td>{fmt(row.ios)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </SectionBody>
          </SectionWrapper>
        </>
      )}
    </Wrapper>
  );
};

export default AdminTrapAppStatistics;
